import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Container, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { UploadFile } from '@mui/icons-material';
import api from '../services/api';
import PriceListImportDialog from '../components/merchant/PriceListImportDialog';

/** Admin → Merchant price lists: catalog health per supplier, and import on their behalf. */
export default function AdminMerchantCatalogsPage() {
  const [merchants, setMerchants] = useState(null);
  const [error, setError] = useState('');
  const [importFor, setImportFor] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/admin/merchant-catalogs');
      setMerchants(res.data.data || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load merchants.');
      setMerchants([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>Merchant price lists</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 720 }}>
        Load a supplier’s price list into their Eqaya catalog. Buyers only see products whose price was
        confirmed within the merchant’s window (14 days by default); an import confirms every price in it.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {merchants === null ? (
        <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
      ) : merchants.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No merchants yet. A supplier signs up as a Builders Merchant first.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Merchant</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Products</TableCell>
                <TableCell align="right">Out-of-date prices</TableCell>
                <TableCell>Last import</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {merchants.map((m) => (
                <TableRow key={m.id} hover>
                  <TableCell>
                    <Typography sx={{ fontWeight: 600 }}>{m.trading_name || m.business_name}</Typography>
                    <Typography variant="caption" color="text.secondary">{m.email}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={m.approval_status === 'approved' && m.account_status === 'Active' ? 'Live' : `${m.approval_status} · ${m.account_status}`}
                      color={m.approval_status === 'approved' && m.account_status === 'Active' ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell align="right">{m.product_count.toLocaleString()}</TableCell>
                  <TableCell align="right">
                    {m.stale_count > 0 ? <Chip size="small" color="warning" label={m.stale_count.toLocaleString()} /> : '0'}
                  </TableCell>
                  <TableCell>{m.last_import_at ? new Date(m.last_import_at).toLocaleDateString() : 'Never'}</TableCell>
                  <TableCell align="right">
                    <Button size="small" startIcon={<UploadFile />} onClick={() => setImportFor(m)}>Import</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {importFor && (
        <PriceListImportDialog open endpoint={`/admin/merchant-catalogs/${importFor.id}/import`}
          merchantName={importFor.trading_name || importFor.business_name}
          onClose={() => setImportFor(null)} onApplied={load} />
      )}
    </Container>
  );
}
